import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, BookOpen, ChevronRight } from "lucide-react";
import Navbar from "@/components/wishire/Navbar";
import Footer from "@/components/wishire/Footer";
import Seo, { pageJsonLd } from "@/components/seo/Seo";
// CommonJS on purpose (shared with scripts/prerender.js, which runs in plain Node).
const content = require("@/content/seo-pages");

/* Renders any SEO content page from content/seo-pages.js. Section HTML is
   authored in this repo (never user input), so dangerouslySetInnerHTML is safe. */

const AUDIENCE = { college: "For placement cells", student: "For students", recruiter: "For recruiters" };

export default function ContentPage() {
  const { pathname } = useLocation();
  const page = content.byPath[pathname.replace(/\/+$/, "") || "/"];
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  if (!page) return <NotFound />;
  const related = (page.related || []).map((p) => content.byPath[p]).filter(Boolean);
  const crumbs = pathname.split("/").filter(Boolean);

  return (
    <div className="App bg-[#FDFBF7] text-stone-900 min-h-screen">
      <Seo title={page.title} description={page.description} path={page.path} type={page.kind === "guide" ? "article" : "website"} jsonLd={pageJsonLd(page)} />
      <Navbar />
      <main className="seo-page">
        <header className="container-x pt-12 md:pt-16 pb-10">
          <nav className="text-xs text-stone-500 flex flex-wrap items-center gap-1" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-stone-900">Home</Link>
            {crumbs.map((c, i) => (
              <span key={c} className="flex items-center gap-1"><ChevronRight size={12} />
                {i < crumbs.length - 1 && content.byPath["/" + crumbs.slice(0, i + 1).join("/")]
                  ? <Link to={"/" + crumbs.slice(0, i + 1).join("/")} className="hover:text-stone-900 capitalize">{c.replace(/-/g, " ")}</Link>
                  : <span className={i === crumbs.length - 1 ? "" : "capitalize"}>{i === crumbs.length - 1 ? page.h1 : c.replace(/-/g, " ")}</span>}
              </span>
            ))}
          </nav>
          <span className="overline mt-6 block">{page.kind === "guide" ? "Guide" : page.kind === "company-test" ? "Company test pattern" : AUDIENCE[page.audience] || "Wishire"}</span>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight mt-3 max-w-4xl">{page.h1}</h1>
          <p className="text-lg md:text-xl text-stone-600 mt-5 max-w-3xl leading-relaxed">{page.intro}</p>
          {page.cta && (
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <CtaLink to={page.cta.to} className="btn-primary">{page.cta.label} <ArrowRight size={16} /></CtaLink>
              <span className="text-sm text-stone-500 max-w-sm">{page.cta.sub}</span>
            </div>
          )}
        </header>

        <div className="container-x grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-12 pb-16">
          <article className="prose-seo max-w-3xl">
            {page.sections.map((s) => (
              <section key={s.h2} className="mt-10 first:mt-0">
                <h2 className="font-display text-2xl md:text-3xl leading-tight">{s.h2}</h2>
                <div className="mt-4 text-stone-700 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: s.html }} />
              </section>
            ))}
            {page.faq && page.faq.length > 0 && (
              <section className="mt-12">
                <h2 className="font-display text-2xl md:text-3xl leading-tight">Questions people ask</h2>
                <dl className="mt-5 divide-y divide-stone-200 border-y border-stone-200">
                  {page.faq.map((f) => (
                    <div key={f.q} className="py-4">
                      <dt className="font-medium text-stone-900">{f.q}</dt>
                      <dd className="mt-1.5 text-stone-600 leading-relaxed">{f.a}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
            {page.cta && (
              <section className="mt-12 rounded-3xl bg-stone-900 text-stone-50 p-8 wsh-grain relative overflow-hidden">
                <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(420px 240px at 90% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
                <div className="relative">
                  <p className="font-display text-2xl md:text-3xl">{page.audience === "college" ? "See your own batch on a live dashboard." : page.audience === "recruiter" ? "Hire from verified readiness." : "Start with one free mock interview."}</p>
                  <p className="text-stone-300 mt-2 max-w-xl">{page.cta.sub}</p>
                  <CtaLink to={page.cta.to} className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 font-medium">{page.cta.label} <ArrowRight size={16} /></CtaLink>
                </div>
              </section>
            )}
          </article>

          <aside className="space-y-6 lg:sticky lg:top-24 self-start">
            {related.length > 0 && (
              <div className="rounded-3xl border border-stone-200 bg-white p-5">
                <span className="overline">Related</span>
                <ul className="mt-3 space-y-2.5">
                  {related.map((r) => <li key={r.path}><Link to={r.path} className="text-sm text-stone-700 hover:text-[#C2410C] leading-snug block">{r.h1}</Link></li>)}
                </ul>
              </div>
            )}
            <div className="rounded-3xl border border-stone-200 bg-white p-5">
              <span className="overline">Guides</span>
              <ul className="mt-3 space-y-2.5">
                {content.guides.filter((g) => g.path !== page.path).slice(0, 5).map((g) => <li key={g.path}><Link to={g.path} className="text-sm text-stone-700 hover:text-[#C2410C] leading-snug block">{g.h1}</Link></li>)}
                <li><Link to="/guides" className="text-sm font-medium text-stone-900 inline-flex items-center gap-1">All guides <ArrowRight size={13} /></Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* /guides index */
export function GuidesIndex() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const jsonLd = { "@context": "https://schema.org", "@type": "CollectionPage", name: "Placement guides", url: "https://wishire.in/guides",
    hasPart: content.guides.map((g) => ({ "@type": "Article", headline: g.h1, url: "https://wishire.in" + g.path })) };
  return (
    <div className="App bg-[#FDFBF7] text-stone-900 min-h-screen">
      <Seo title="Campus Placement Guides for Students and Placement Cells" description="Practical guides: how to improve campus placement rate, the placement readiness score explained, ATS résumé for freshers, TCS NQT preparation, interview questions, NAAC and NIRF placement reports." path="/guides" jsonLd={jsonLd} />
      <Navbar />
      <main className="container-x pt-12 md:pt-16 pb-16">
        <span className="overline">Guides</span>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight mt-3 max-w-3xl">Placement, explained plainly.</h1>
        <p className="text-lg text-stone-600 mt-5 max-w-2xl">For students chasing a first job and the placement cells getting them there. No fluff, benchmarks included.</p>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {content.guides.map((g) => (
            <Link key={g.path} to={g.path} className="rounded-3xl border border-stone-200 bg-white p-6 hover:border-stone-900 transition-colors block">
              <span className="text-[11px] font-mono uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5"><BookOpen size={12} /> {g.audience === "college" ? "Placement cells" : "Students"}</span>
              <h2 className="font-display text-2xl mt-2 leading-tight">{g.h1}</h2>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">{g.description}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium">Read <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function CtaLink({ to, className, children }) {
  return to.startsWith("/#") ? <a href={to} className={className}>{children}</a> : <Link to={to} className={className}>{children}</Link>;
}

function NotFound() {
  return (
    <div className="App bg-[#FDFBF7] min-h-screen">
      <Seo title="Page not found" description="" noindex />
      <Navbar />
      <main className="container-x py-24 text-center"><h1 className="font-display text-4xl">That page isn't here.</h1><Link to="/" className="btn-primary mt-6 inline-flex">Back to Wishire</Link></main>
      <Footer />
    </div>
  );
}
